import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth, defaultPathFor } from "@/lib/auth";

import LoginPage from "@/pages/LoginPage";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import ParksPage from "@/pages/admin/ParksPage";
import SupervisorsPage from "@/pages/admin/SupervisorsPage";
import ContractorsPage from "@/pages/admin/ContractorsPage";
import EmployeesPage from "@/pages/admin/EmployeesPage";
import PlannerPage from "@/pages/admin/PlannerPage";
import ReportsPage from "@/pages/admin/ReportsPage";
import MonthlyReportsPage from "@/pages/admin/MonthlyReportsPage";
import ReportGroupsPage from "@/pages/admin/ReportGroupsPage";
import SalaryReportPage from "@/pages/admin/SalaryReportPage";
import PayrollRatesPage from "@/pages/admin/PayrollRatesPage";
import SettingsPage from "@/pages/admin/SettingsPage";
import AttendanceHubPage from "@/pages/admin/AttendanceHubPage";
import AttendanceRolePage from "@/pages/admin/AttendanceRolePage";
import AttendanceComparePage from "@/pages/admin/AttendanceComparePage";
import AttendanceCalendarPage from "@/pages/admin/AttendanceCalendarPage";
import HolidaysPage from "@/pages/admin/HolidaysPage";
import PermissionsPage from "@/pages/admin/PermissionsPage";
import SupervisorAttendancePage from "@/pages/admin/SupervisorAttendancePage";
import GpsTrailPage from "@/pages/admin/GpsTrailPage";
import FeedbackPage from "@/pages/admin/FeedbackPage";
import SupervisorMonitoringPage from "@/pages/admin/SupervisorMonitoringPage";
import ParkAssetsPage from "@/pages/admin/ParkAssetsPage";
import CleaningProgressPage from "@/pages/admin/CleaningProgressPage";
import CleaningCyclesOverviewPage from "@/pages/admin/CleaningCyclesOverviewPage";
import CleaningCyclesParkPage from "@/pages/admin/CleaningCyclesParkPage";
import CleaningLocationRequestsPage from "@/pages/admin/CleaningLocationRequestsPage";
import SupervisorInchargePage from "@/pages/admin/SupervisorInchargePage";
import StorageManagementPage from "@/pages/admin/StorageManagementPage";
import AdminHourlySelfiePage from "@/pages/admin/AdminHourlySelfiePage";
import SupervisorSchedulesPage from "@/pages/admin/SupervisorSchedulesPage";
import CleaningQuestionsPage from "@/pages/admin/CleaningQuestionsPage";
import AdminDevicesPage from "@/pages/admin/AdminDevicesPage";
import AdminWorkersAtWorkPage from "@/pages/admin/AdminWorkersAtWorkPage";
import DeploySnapshotPage from "@/pages/admin/DeploySnapshotPage";
import SettingsGate from "@/components/SettingsGate";

import PmcTodayPage from "@/pages/pmc/PmcTodayPage";
import PmcAttendancePage from "@/pages/pmc/PmcAttendancePage";
import PmcCleaningPage from "@/pages/pmc/PmcCleaningPage";
import PmcSalaryVerificationPage from "@/pages/pmc/PmcSalaryVerificationPage";

import ContractorAttendancePage from "@/pages/contractor/ContractorAttendancePage";
import ContractorAdminDashboardPage from "@/pages/contractor-admin/ContractorAdminDashboardPage";

function Protected({ roles, children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-10 text-sm text-muted-foreground">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to={defaultPathFor(user.role, user)} replace />;
  return children;
}

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-10 text-sm text-muted-foreground">Loading…</div>;
  return <Navigate to={user ? defaultPathFor(user.role, user) : "/login"} replace />;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Toaster richColors closeButton position="top-right" />
          <Routes>
            <Route path="/" element={<RootRedirect />} />
            <Route path="/login" element={<LoginPage />} />

            {/* Admin */}
            <Route path="/admin/dashboard" element={<Protected roles={["admin"]}><AdminDashboard /></Protected>} />
            <Route path="/admin/parks" element={<Protected roles={["admin"]}><SettingsGate><ParksPage /></SettingsGate></Protected>} />
            <Route path="/admin/supervisors" element={<Protected roles={["admin"]}><SettingsGate><SupervisorsPage /></SettingsGate></Protected>} />
            <Route path="/admin/contractors" element={<Protected roles={["admin"]}><SettingsGate><ContractorsPage /></SettingsGate></Protected>} />
            <Route path="/admin/employees" element={<Protected roles={["admin"]}><SettingsGate><EmployeesPage /></SettingsGate></Protected>} />
            <Route path="/admin/park-assets" element={<Protected roles={["admin"]}><SettingsGate><ParkAssetsPage /></SettingsGate></Protected>} />
            <Route path="/admin/cleaning-progress" element={<Protected roles={["admin"]}><CleaningProgressPage /></Protected>} />
            <Route path="/admin/workers-at-work" element={<Protected roles={["admin"]}><AdminWorkersAtWorkPage /></Protected>} />
            <Route path="/admin/deploy-snapshot" element={<Protected roles={["admin"]}><SettingsGate><DeploySnapshotPage /></SettingsGate></Protected>} />
            <Route path="/admin/cleaning-location-requests" element={<Protected roles={["admin"]}><CleaningLocationRequestsPage /></Protected>} />
            <Route path="/admin/supervisor-incharge" element={<Protected roles={["admin"]}><SettingsGate><SupervisorInchargePage /></SettingsGate></Protected>} />
            <Route path="/admin/cleaning-cycles" element={<Protected roles={["admin"]}><CleaningCyclesOverviewPage /></Protected>} />
            <Route path="/admin/cleaning-cycles/:parkId" element={<Protected roles={["admin"]}><CleaningCyclesParkPage /></Protected>} />
            <Route path="/admin/storage" element={<Protected roles={["admin"]}><SettingsGate><StorageManagementPage /></SettingsGate></Protected>} />
            <Route path="/admin/devices" element={<Protected roles={["admin"]}><AdminDevicesPage /></Protected>} />
            <Route path="/admin/planner" element={<Protected roles={["admin"]}><PlannerPage /></Protected>} />
            <Route path="/admin/validation" element={<Navigate to="/admin/dashboard" replace />} />
            <Route path="/admin/attendance" element={<Protected roles={["admin"]}><AttendanceHubPage /></Protected>} />
            <Route path="/admin/attendance/pmc" element={<Protected roles={["admin"]}><AttendanceRolePage role="pmc_supervisor" /></Protected>} />
            <Route path="/admin/attendance/contractor" element={<Protected roles={["admin"]}><AttendanceRolePage role="contractor_supervisor" /></Protected>} />
            <Route path="/admin/attendance/compare" element={<Protected roles={["admin"]}><AttendanceComparePage /></Protected>} />
            <Route path="/admin/attendance/calendar" element={<Protected roles={["admin"]}><AttendanceCalendarPage /></Protected>} />
            <Route path="/admin/attendance/permissions" element={<Protected roles={["admin"]}><PermissionsPage /></Protected>} />
            <Route path="/admin/attendance/supervisors" element={<Protected roles={["admin"]}><SupervisorAttendancePage /></Protected>} />
            <Route path="/admin/attendance/itcot-hourly" element={<Protected roles={["admin"]}><AdminHourlySelfiePage /></Protected>} />
            <Route path="/admin/supervisor-schedules" element={<Protected roles={["admin"]}><SettingsGate><SupervisorSchedulesPage /></SettingsGate></Protected>} />
            <Route path="/admin/cleaning-questions" element={<Protected roles={["admin"]}><SettingsGate><CleaningQuestionsPage /></SettingsGate></Protected>} />
            <Route path="/admin/gps" element={<Protected roles={["admin"]}><GpsTrailPage /></Protected>} />
            <Route path="/admin/supervisor-monitoring" element={<Protected roles={["admin"]}><SupervisorMonitoringPage /></Protected>} />
            <Route path="/admin/feedback" element={<Protected roles={["admin"]}><FeedbackPage /></Protected>} />
            <Route path="/admin/holidays" element={<Protected roles={["admin"]}><SettingsGate><HolidaysPage /></SettingsGate></Protected>} />
            <Route path="/admin/reports" element={<Protected roles={["admin"]}><ReportsPage /></Protected>} />
            <Route path="/admin/monthly-reports" element={<Protected roles={["admin"]}><MonthlyReportsPage /></Protected>} />
            <Route path="/admin/report-groups" element={<Protected roles={["admin"]}><SettingsGate><ReportGroupsPage /></SettingsGate></Protected>} />
            <Route path="/admin/salary-report" element={<Protected roles={["admin"]}><SalaryReportPage /></Protected>} />
            <Route path="/admin/payroll-rates" element={<Protected roles={["admin"]}><SettingsGate><PayrollRatesPage /></SettingsGate></Protected>} />
            <Route path="/admin/settings" element={<Protected roles={["admin"]}><SettingsPage /></Protected>} />

            {/* PMC */}
            <Route path="/pmc/today" element={<Protected roles={["pmc_supervisor"]}><PmcTodayPage /></Protected>} />
            <Route path="/pmc/attendance" element={<Protected roles={["pmc_supervisor"]}><PmcAttendancePage /></Protected>} />
            <Route path="/pmc/cleaning" element={<Protected roles={["pmc_supervisor"]}><PmcCleaningPage /></Protected>} />
            <Route path="/pmc/salary-verification" element={<Protected roles={["pmc_supervisor"]}><PmcSalaryVerificationPage /></Protected>} />
            <Route path="/pmc/feedback" element={<Protected roles={["pmc_supervisor"]}><FeedbackPage /></Protected>} />

            {/* Contractor */}
            <Route path="/contractor/attendance" element={<Protected roles={["contractor_supervisor", "supervisor_incharge"]}><ContractorAttendancePage /></Protected>} />

            {/* Contractor Admin (read-only office login) */}
            <Route path="/contractor-admin/dashboard" element={<Protected roles={["contractor_admin"]}><ContractorAdminDashboardPage /></Protected>} />
            <Route path="/contractor-admin/work-captures" element={<Protected roles={["contractor_admin"]}><AttendanceRolePage role="contractor_supervisor" /></Protected>} />
            <Route path="/contractor-admin/workers-at-work" element={<Protected roles={["contractor_admin"]}><AdminWorkersAtWorkPage /></Protected>} />
            <Route path="/contractor-admin/attendance-calendar" element={<Protected roles={["contractor_admin"]}><AttendanceCalendarPage /></Protected>} />
            <Route path="/contractor-admin/feedback" element={<Protected roles={["contractor_admin"]}><FeedbackPage /></Protected>} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
